import { hasCaseRequiredAndQuantity } from '@app/pdp/utils';
import {
    isCustomPharmacyOOS,
    hasStock,
    validateStockStatus,
} from '@libs/shared-ui';
import type { AsyncStore } from '@libs/common-node';
import type {
    MappedProductVariant,
    ProductVariantInventory,
} from '@libs/algolia';
import {
    SELECTED_VARIANT_KEYS,
    createProductVariantInventoryKey,
} from '@libs/algolia';

import { DeliveryType, capitalize } from '@libs/core';
import { StockStatusEnum } from '@petsmart-ui/sparky';

export const handleVariantDisplay = (variantKey: string) => {
    switch (true) {
        case SELECTED_VARIANT_KEYS.THREAD_COLORS === variantKey:
            return 'Thread color';
        case SELECTED_VARIANT_KEYS.FONT_FACE === variantKey:
            return 'Font';
        default:
            return capitalize(variantKey);
    }
};

const variantCaseQtyForDelivery = (
    variant: MappedProductVariant,
    deliveryMethod: DeliveryType
): number => {
    const minValidCaseQty = 1;

    if (deliveryMethod === DeliveryType.BOPIS) {
        return variant.bopisCaseQty ?? minValidCaseQty;
    }

    return hasCaseRequiredAndQuantity(variant)
        ? variant.customCaseQuantity ?? minValidCaseQty
        : minValidCaseQty;
};

export const otherInventoriesHaveStock = (
    inventories: ProductVariantInventory,
    objectID: string
) => {
    const webUsInventories = inventories[`${objectID}-ps_us_web_inv`];
    const webCaInventories = inventories[`${objectID}-ps_ca_web_inv`];

    return (
        (webUsInventories && webUsInventories.ats > 0) ||
        (webCaInventories && webCaInventories.ats > 0)
    );
};

export const checkProductVariantStock = (
    deliveryMethod: DeliveryType,
    variant: MappedProductVariant,
    inventories?: ProductVariantInventory,
    selectedStore?: AsyncStore
) => {
    if (
        !inventories ||
        !variant ||
        isCustomPharmacyOOS(variant.customPharmacyOOS)
    ) {
        return false;
    }

    const inventoryKey = createProductVariantInventoryKey(
        variant.objectID,
        deliveryMethod
    );
    const variantInventory = inventories[inventoryKey];

    if (
        !variantInventory &&
        deliveryMethod === DeliveryType.BOPIS &&
        !selectedStore?.store
    ) {
        return true;
    }

    if (!variantInventory) {
        return otherInventoriesHaveStock(inventories, variant.objectID);
    }

    const storeStockStatus = validateStockStatus({
        stockQuantity: variantInventory.ats,
        minimumCaseQty: variantCaseQtyForDelivery(variant, deliveryMethod),
    });

    if (
        storeStockStatus === StockStatusEnum.OutOfStock &&
        deliveryMethod === DeliveryType.BOPIS &&
        otherInventoriesHaveStock(inventories, variant.objectID)
    ) {
        return true;
    }

    return hasStock(storeStockStatus);
};
